import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["textField", "input", "option"];

  static values = {};

  currentTextValue = "";
  currentInputValue = "";

  declare readonly textFieldTarget: HTMLInputElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly optionTargets: HTMLDivElement[];

  mutationObserver: MutationObserver;

  connect() {
    this.textFieldTarget.addEventListener("focus", this.handleTextFieldFocus);
    this.textFieldTarget.addEventListener("input", this.handleTextFieldInput);
    this.currentTextValue = this.textFieldTarget.value;
    this.highlightSelected();
  }

  disconnect() {
    this.textFieldTarget.removeEventListener(
      "focus",
      this.handleTextFieldFocus
    );
    this.textFieldTarget.removeEventListener(
      "input",
      this.handleTextFieldInput
    );
  }

  handleTextFieldFocus = (e: FocusEvent) => {
    if (e.target instanceof HTMLInputElement) {
      e.target.select();
    }
  };

  inputTargetConnected() {
    // this observes the input value and updates the text field value
    // when the input value changes
    this.mutationObserver = new MutationObserver((mutations) => {
      if (
        mutations[0].attributeName == "value" &&
        this.currentInputValue != this.inputTarget.value
      ) {
        const textValue =
          this.optionTargets.find(
            (option) => option.dataset.value === this.inputTarget.value
          )?.dataset.label ?? "";
        if (textValue !== "") {
          this.currentInputValue = this.inputTarget.value;
          this.currentTextValue = this.textFieldTarget.value = textValue;
        } else {
          this.inputTarget.value = this.currentInputValue;
        }
      }
    });

    this.mutationObserver.observe(this.inputTarget, {
      attributes: true
    });
  }

  inputTargetDisconnected() {
    this.mutationObserver?.disconnect();
  }

  handleTextFieldInput = (e: Event) => {
    const searchText =
      e.target instanceof HTMLInputElement
        ? e.target.value.toLocaleLowerCase()
        : "";

    this.optionTargets.forEach((option) => {
      const optionTerms = (option.dataset.searchPath ?? "")
        .toLocaleLowerCase()
        .split("||");
      const searchTerms = searchText.split("s+");
      if (
        searchTerms.every((searchTerm) =>
          optionTerms.some((optionTerm) => optionTerm.search(searchTerm) >= 0)
        )
      ) {
        option.classList.remove("hidden");
      } else {
        option.classList.add("hidden");
        option.setAttribute("tabIndex", "-1");
      }
    });

    this.optionTargets
      .find(
        (option) =>
          window.getComputedStyle(option).getPropertyValue("display") != "none"
      )
      ?.setAttribute("tabIndex", "0");
  };

  resetItems() {
    this.textFieldTarget.value = this.currentTextValue;
    this.optionTargets.forEach((option) => {
      option.classList.remove("hidden");
    });
  }

  handleSelect(e: KeyboardEvent | PointerEvent) {
    if (e.target instanceof HTMLElement) {
      this.inputTarget.value = this.currentInputValue =
        e.target.dataset.value ?? "";
      this.textFieldTarget.value = this.currentTextValue =
        e.target.dataset.label ?? "";
      this.textFieldTarget.dispatchEvent(
        new Event("change", { bubbles: true })
      );
      this.highlightSelected();
    }
  }

  highlightSelected() {
    this.optionTargets.forEach((option) => {
      if (option.dataset.value === this.inputTarget.value) {
        option.classList.add("text-primary");
        option.setAttribute("tabIndex", "0");
      } else {
        option.classList.remove("text-primary");
        option.setAttribute("tabIndex", "-1");
      }
    });
  }

  handleItemKeyUp(e: KeyboardEvent) {
    if (e.code === "Enter") {
      this.handleSelect(e);
      this.element.dispatchEvent(new CustomEvent("dropdownSelect:selected"));
    }
  }
}
